import { useEffect, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Text } from "@/components/typography";
import { Button } from "./Button";
import styles from "./ui.module.css";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  children,
  className,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden />
      <aside
        className={cn(styles.drawer, className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Drawer"}
      >
        <div className={styles.drawerHeader}>
          <Text role="title" as="h2">
            {title}
          </Text>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            Close
          </Button>
        </div>
        <div className={styles.drawerBody}>{children}</div>
      </aside>
    </>
  );
}
